let activeAnimationId: number | null = null;
let activeCleanup: (() => void) | null = null;

function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function findFirstErrorName(errors: Record<string, any>, prefix = ''): string | null {
    for (const key of Object.keys(errors)) {
        const val = errors[key];
        if (!val) continue;
        const currentPath =
            /^\d+$/.test(key) && prefix
                ? `${prefix}[${key}]`
                : prefix
                ? `${prefix}.${key}`
                : key;
        if (typeof val === 'object') {
            if (val.message || val.type) {
                return currentPath;
            }
            const nested = findFirstErrorName(val, currentPath);
            if (nested) return nested;
        }
    }
    return null;
}

export function isElementScrollable(el: HTMLElement): boolean {
    if (!el) return false;
    const hasOverflow = el.scrollHeight > el.clientHeight + 2;
    if (!hasOverflow) return false;
    if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
        const style = window.getComputedStyle(el);
        const overflowY = style.overflowY || style.overflow;
        return overflowY === 'auto' || overflowY === 'scroll';
    }
    return true;
}

export function getScrollContainer(element: HTMLElement): HTMLElement {
    const dialogContent = element.closest<HTMLElement>('.MuiDialogContent-root');
    if (dialogContent) {
        return dialogContent;
    }

    let parent = element.parentElement;
    while (parent && parent !== document.body && parent !== document.documentElement) {
        if (isElementScrollable(parent)) {
            return parent;
        }
        parent = parent.parentElement;
    }

    return (document.scrollingElement as HTMLElement) || document.documentElement || document.body;
}

export function isWindowContainer(container: HTMLElement): boolean {
    return (
        !container ||
        container === document.documentElement ||
        container === document.body ||
        container === document.scrollingElement
    );
}

export function getScrollTop(container: HTMLElement): number {
    if (isWindowContainer(container)) {
        return (
            (typeof window !== 'undefined' && (window.pageYOffset !== undefined ? window.pageYOffset : window.scrollY)) ||
            document.documentElement?.scrollTop ||
            document.body?.scrollTop ||
            0
        );
    }
    return container.scrollTop;
}

export function setScrollTop(container: HTMLElement, top: number): void {
    if (isWindowContainer(container)) {
        if (document.documentElement) document.documentElement.scrollTop = top;
        if (document.body) document.body.scrollTop = top;
        try {
            if (
                typeof window !== 'undefined' &&
                typeof window.scrollTo === 'function' &&
                (typeof process === 'undefined' || process.env.NODE_ENV !== 'test')
            ) {
                window.scrollTo(0, top);
            }
        } catch (e) {
        }
    } else {
        container.scrollTop = top;
    }
}

export function smoothSlideTo(
    container: HTMLElement,
    targetTop: number,
    duration = 450,
    onComplete?: () => void
): void {
    if (activeCleanup) {
        activeCleanup();
        activeCleanup = null;
    }
    if (activeAnimationId !== null && typeof window !== 'undefined' && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(activeAnimationId);
        activeAnimationId = null;
    }

    const startTop = getScrollTop(container);
    const distance = targetTop - startTop;

    if (Math.abs(distance) < 2 || typeof window === 'undefined' || !window.requestAnimationFrame) {
        setScrollTop(container, targetTop);
        if (onComplete) onComplete();
        return;
    }

    const prevScrollBehavior = container.style ? container.style.scrollBehavior : '';
    if (container.style) {
        container.style.scrollBehavior = 'auto';
    }

    const cleanup = () => {
        if (container.style) {
            container.style.scrollBehavior = prevScrollBehavior;
        }
        if (typeof window !== 'undefined' && window.removeEventListener) {
            window.removeEventListener('wheel', cancelOnUserInteraction);
            window.removeEventListener('touchmove', cancelOnUserInteraction);
        }
        activeCleanup = null;
    };

    const cancelOnUserInteraction = () => {
        if (activeAnimationId !== null && typeof window !== 'undefined' && window.cancelAnimationFrame) {
            window.cancelAnimationFrame(activeAnimationId);
            activeAnimationId = null;
        }
        cleanup();
    };

    if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('wheel', cancelOnUserInteraction, { passive: true });
        window.addEventListener('touchmove', cancelOnUserInteraction, { passive: true });
    }
    activeCleanup = cleanup;

    let startTime: number | null = null;

    const step = (currentTime: number) => {
        if (startTime === null) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = easeInOutCubic(progress);

        setScrollTop(container, startTop + distance * ease);

        if (progress < 1) {
            activeAnimationId = window.requestAnimationFrame(step);
        } else {
            activeAnimationId = null;
            setScrollTop(container, targetTop);
            cleanup();
            if (onComplete) onComplete();
        }
    };

    activeAnimationId = window.requestAnimationFrame(step);
}

export const scrollToFirstError = (
    formElement?: HTMLElement | null,
    errors?: Record<string, any>,
    delay = 50
): void => {
    setTimeout(() => {
        const form =
            (formElement?.tagName === 'FORM' ? formElement : formElement?.closest('form')) ||
            document.querySelector('form');

        if (!form) return;

        let errorEl = form.querySelector(
            '.Mui-error, [aria-invalid="true"], .MuiFormHelperText-root.Mui-error, input:invalid'
        );

        if (!errorEl && errors) {
            const errorFieldName = findFirstErrorName(errors);
            if (errorFieldName) {
                const escapedName = errorFieldName.replace(/\./g, '\\.');
                errorEl = form.querySelector(`[name="${escapedName}"], [name="${errorFieldName}"]`);
            }
        }

        if (!errorEl) return;

        const target =
            errorEl.closest<HTMLElement>('.MuiFormControl-root') ||
            errorEl.closest<HTMLElement>('.MuiGrid-item') ||
            (errorEl as HTMLElement);

        const container = getScrollContainer(target);
        let targetTop = 0;

        if (isWindowContainer(container)) {
            const targetRect = target.getBoundingClientRect();
            targetTop = Math.max(0, targetRect.top + getScrollTop(container) - 24);
        } else {
            const targetRect = target.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            targetTop = Math.max(
                0,
                getScrollTop(container) + (targetRect.top - containerRect.top) - 24
            );
        }

        smoothSlideTo(container, targetTop, 450, () => {
            const input = target.querySelector<HTMLElement>(
                'input:not([type="hidden"]):not([tabindex="-1"]), select, textarea, button, [role="button"], [role="combobox"], [tabindex]:not([tabindex="-1"])'
            );
            if (input && typeof input.focus === 'function') {
                input.focus({ preventScroll: true });
            }
        });
    }, delay);
};
